import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, X } from '@phosphor-icons/react';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { isBabyPatient } from '@/lib/utils';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const RATINGS = [
  { value: 1, label: 'Muito insatisfeita', emoji: '😞' },
  { value: 2, label: 'Insatisfeita', emoji: '😕' },
  { value: 3, label: 'Neutra', emoji: '😐' },
  { value: 4, label: 'Satisfeita', emoji: '🙂' },
  { value: 5, label: 'Muito satisfeita', emoji: '😊' },
];

export function Scale0to10({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <input type="range" min="0" max="10" step="1" value={value ?? 5}
        onChange={e => onChange(parseInt(e.target.value))}
        className="flex-1 accent-brand-green" data-testid={`${testid}-slider`} />
      <span className="text-sm font-semibold text-brand-green w-7 text-center" data-testid={`${testid}-value`}>{value ?? 5}</span>
    </div>
  );
}

export default function ConsultationFeedback() {
  const { user } = useAuth();
  const isSuperBebes = isBabyPatient(user);
  const [pending, setPending] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [planSpeed, setPlanSpeed] = useState(5);
  const [techQuality, setTechQuality] = useState(5);
  const [materialsUseful, setMaterialsUseful] = useState(5);
  const [menuOpinion, setMenuOpinion] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/my-pending-feedback`);
        if (data.pending) setPending(data.consultation);
      } catch {}
    })();
  }, []);

  const submit = async () => {
    if (!rating) { toast.error('Seleciona a tua satisfação'); return; }
    try {
      const payload = { consultation_id: pending.id, rating, comment };
      if (isSuperBebes) {
        payload.menu_opinion = menuOpinion;
      } else {
        payload.plan_speed = planSpeed;
        payload.tech_quality = techQuality;
        payload.materials_useful = materialsUseful;
      }
      await axios.post(`${API}/api/consultation-feedback`, payload);
      toast.success('Obrigada pelo teu feedback!');
      setPending(null);
    } catch { toast.error('Erro ao enviar'); }
  };

  if (!pending || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" data-testid="consultation-feedback-modal">
      <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl relative">
        <button onClick={() => setDismissed(true)} className="absolute top-4 right-4 text-brand-muted hover:text-brand-text" data-testid="dismiss-feedback"><X size={18} /></button>

        <div className="text-center mb-5">
          <Star size={32} className="mx-auto text-brand-gold mb-2" weight="fill" />
          <h3 className="font-heading font-medium text-lg text-brand-text">Como correu a consulta?</h3>
          <p className="text-xs text-brand-muted mt-1">Consulta de {fmtDatePt(pending.date)}</p>
        </div>

        {/* Rating scale */}
        <div className="flex justify-between mb-5 px-2">
          {RATINGS.map(r => (
            <button key={r.value} onClick={() => setRating(r.value)}
              className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${rating === r.value ? 'bg-brand-green/10 scale-110' : 'hover:bg-brand-bg'}`}
              data-testid={`rating-${r.value}`}>
              <span className="text-2xl">{r.emoji}</span>
              <span className={`text-[9px] ${rating === r.value ? 'text-brand-green font-medium' : 'text-brand-muted'}`}>{r.label}</span>
            </button>
          ))}
        </div>

        {/* New questions per program */}
        {isSuperBebes ? (
          <div className="mb-4" data-testid="sb-question">
            <p className="text-xs text-brand-muted mb-2">O que achas da ementa proposta para o teu bebé?</p>
            <Textarea value={menuOpinion} onChange={e => setMenuOpinion(e.target.value)} placeholder="Partilha a tua opinião..." className="rounded-xl border-brand-border text-sm" rows={2} data-testid="feedback-menu-opinion" />
          </div>
        ) : (
          <div className="space-y-3 mb-4">
            <div>
              <p className="text-xs text-brand-muted mb-1">Rapidez de entrega do plano alimentar? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={planSpeed} onChange={setPlanSpeed} testid="feedback-plan-speed" />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Qualidade das respostas técnicas e científicas? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={techQuality} onChange={setTechQuality} testid="feedback-tech-quality" />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Os materiais de apoio foram úteis? <span className="text-brand-placeholder">(0–10)</span></p>
              <Scale0to10 value={materialsUseful} onChange={setMaterialsUseful} testid="feedback-materials-useful" />
            </div>
          </div>
        )}

        {/* Comment */}
        <div className="mb-4">
          <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Algo que queiras partilhar? (opcional)" className="rounded-xl border-brand-border text-sm" rows={3} data-testid="feedback-comment" />
        </div>

        <button onClick={submit} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="submit-feedback-btn">
          Enviar feedback
        </button>
        <button onClick={() => setDismissed(true)} className="w-full text-center text-xs text-brand-muted mt-2 py-1 hover:text-brand-text">
          Agora não
        </button>
      </div>
    </div>
  );
}
